/**
 * Column distribution utilities for item grid layouts
 * Distributes items across multiple columns for balanced, independent column behavior
 */

/**
 * Distributes items evenly across columns using round-robin distribution
 * This maintains left-to-right, top-to-bottom visual order
 *
 * Example with 6 items and 3 columns:
 * Column 1: items[0], items[3]
 * Column 2: items[1], items[4]
 * Column 3: items[2], items[5]
 *
 * @param items - Array of items to distribute
 * @param columnCount - Number of columns to create
 * @returns Array of column arrays, each containing items for that column
 */
export function distributeItemsIntoColumns<T>(
  items: T[],
  columnCount: number
): T[][] {
  // Handle edge cases
  if (columnCount <= 0) return [];
  if (columnCount === 1) return [items];
  if (items.length === 0) return Array.from({ length: columnCount }, () => []);

  // Initialize empty columns
  const columns: T[][] = Array.from({ length: columnCount }, () => []);

  // Round-robin distribution:
  // First item -> column 0, second -> column 1, ..., nth -> column (n % columnCount)
  items.forEach((item, index) => {
    const columnIndex = index % columnCount;
    columns[columnIndex].push(item);
  });

  return columns;
}

/**
 * Get appropriate column count based on viewport width
 * Matches responsive breakpoints used throughout the app
 *
 * Breakpoints:
 * - < 640px:  1 column  (mobile)
 * - >= 640px: 2 columns (tablet)
 * - >= 1024px: 3 columns (desktop)
 * - >= 1280px: 4 columns (wide desktop)
 *
 * @param width - Current viewport width in pixels
 * @returns Number of columns to display
 */
export function getColumnCount(width: number): number {
  if (width < 640) return 1;
  if (width < 1024) return 2;
  if (width < 1280) return 3;
  return 4;
}
